import { useState, useEffect } from 'react';
import apiClient from '../../../services/api/client';
import { useClassifyKeywords, useClassificationStatus } from '../../../services/module2Service';
import { ClassificationResults } from '../types';

interface UseClassificationProcessProps {
  clientId?: string;
  workflowId?: string;
  externalId?: string;
}

export const useClassificationProcess = ({ clientId, workflowId, externalId }: UseClassificationProcessProps) => {
  const [isClassifying, setIsClassifying] = useState(false);
  const [classificationResults, setClassificationResults] = useState<ClassificationResults | null>(null);
  const [classificationId, setClassificationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Classification hooks
  const classifyKeywordsMutation = useClassifyKeywords();
  const { data: classificationStatus } = useClassificationStatus(
    classificationId || '', 
    workflowId || ''
  );

  // Watch classification status
  useEffect(() => {
    if (classificationStatus?.status === 'completed' && classificationStatus.results) {
      setClassificationResults(classificationStatus.results);
      setIsClassifying(false);
    } else if (classificationStatus?.status === 'failed') {
      setError(`Classification failed: ${classificationStatus.error || 'Unknown error'}`);
      setIsClassifying(false);
    }
  }, [classificationStatus]);

  // Start classification with selected files
  const startClassification = async (selectedStemWordFile: string, selectedKeywordFile: string) => {
    if (!selectedStemWordFile || !selectedKeywordFile || !clientId) {
      setError(`Please select ${!selectedStemWordFile ? 'a stem words file' : 'a keywords file'}`);
      return;
    }
    
    setIsClassifying(true);
    setError(null);
    setClassificationResults(null);
    
    try {
      // 1. Fetch the stem words JSON
      const stemWordsResponse = await apiClient.get('/api/v1/storage/get', { 
        params: {
          path: selectedStemWordFile,
          container: 'client-data',
          client_id: clientId
        }
      });
      
      // 2. Fetch the keywords CSV
      const keywordsResponse = await apiClient.get('/api/v1/storage/get', {
        params: {
          path: selectedKeywordFile,
          container: 'client-data',
          client_id: clientId
        }
      });
      
      // 3. Parse CSV to extract keywords
      const csvLines = keywordsResponse.data.split('\n');
      const keywords: string[] = [];
      
      // Parse headers to find the keyword column index
      const headers = csvLines[0].toLowerCase().split(',').map((h: string) => h.trim().replace(/"/g, ''));
      let keywordColumnIndex = headers.findIndex((h: string) => 
        h === 'keyword' || 
        h === 'query' || 
        h === 'keywords' ||
        h === 'search_term' ||
        h === 'term'
      );
      
      // If no keyword column found, default to first column but log a warning
      if (keywordColumnIndex === -1) {
        console.warn('No keyword column found in CSV headers:', headers);
        // Check if there's a header that looks like it might be the keyword column
        // This handles cases where column names might be different
        const possibleKeywordIndex = headers.findIndex((h: string) => 
          h.includes('keyword') || 
          h.includes('query') || 
          h.includes('search') ||
          h.includes('term')
        );
        
        if (possibleKeywordIndex !== -1) {
          keywordColumnIndex = possibleKeywordIndex;
          console.log(`Found possible keyword column at index ${keywordColumnIndex} (column: ${headers[keywordColumnIndex]})`);
        } else {
          // Default to first column if no keyword-related column found
          keywordColumnIndex = 0;
          console.warn('Defaulting to first column as keyword column');
        }
      } else {
        console.log(`Found keyword column at index ${keywordColumnIndex} (column: ${headers[keywordColumnIndex]})`);
      }
      
      // Extract keywords from the identified column
      for (let i = 1; i < csvLines.length; i++) {
        const line = csvLines[i].trim();
        if (line) {
          // Properly parse CSV line considering quoted values
          const parts = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
          if (parts.length > keywordColumnIndex) {
            const keyword = parts[keywordColumnIndex].trim().replace(/^"(.*)"$/, '$1');
            if (keyword) {
              keywords.push(keyword);
            }
          }
        }
      }
      
      console.log(`Extracted ${keywords.length} keywords from CSV`);
      if (keywords.length > 0) {
        console.log('Sample keywords:', keywords.slice(0, 5));
      }
      
      // 4. Send classification request
      const response = await classifyKeywordsMutation.mutateAsync({
        topics: stemWordsResponse.data.topics, 
        keywords: keywords,
        workflowId: externalId || workflowId || '',
        clientId: clientId
      });
      
      setClassificationId(response.classification_id);
    } catch (err) {
      console.error('Error during classification:', err);
      setError('Failed to classify keywords. Please try again.');
      setIsClassifying(false);
    }
  };

  // Function to export results to CSV
  const exportResults = () => {
    if (!classificationResults || !workflowId) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "keyword,topic,subtopic,relevance,confidence,location\r\n";
    
    const allResults = [
      ...classificationResults.relevant,
      ...classificationResults.undecided,
      ...classificationResults.outlier
    ];
    
    allResults.forEach(row => {
      csvContent += `"${row.keyword}","${row.topic}","${row.subtopic}",${row.relevance},${row.confidence},${row.location}\r\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `classification_results_${workflowId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    isClassifying,
    classificationResults,
    error,
    startClassification,
    exportResults,
    setError
  };
};
